import { FC, useMemo } from 'react';
import { Col, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import { FieldMetaProps } from 'formik';
import OptionTooltip from 'components/ui/base/searchbar/search-options/OptionTooltip';
import { Customer } from 'store/customers/customer';
import FormAutocomplete from '../formAutocomplete/FormAutocomplete';

interface Props {
    disabled: boolean;
    customer?: Customer;
    customers: Customer[];
    handleSelectCustomer: Function;
    setCustomer: Function;
    inputStyle?: React.CSSProperties;
    fieldMeta?: FieldMetaProps<any>;
    search: (query: string) => void;
    onBlur?: () => void;
    dropdownMatchSelectWidth?: boolean;
    value?: string | undefined;
    onClear?: () => void;
    placeholder?: string;
}
const CustomerAutocomplete: FC<Props> = ({
    customers,
    handleSelectCustomer,
    setCustomer,
    value,
    disabled,
    inputStyle,
    fieldMeta,
    search,
    onBlur,
    onClear,
    dropdownMatchSelectWidth,
    placeholder,
}) => {
    const { t } = useTranslation('customs_declarations');

    const disableSelect = useMemo(() => disabled, [disabled]);
    const customerOptions = useMemo(() => {
        const options = customers.map((customer) => ({
            value: customer.id,
            label: (
                <Row justify="space-between" align="middle" wrap={false} gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                    <Col>
                        <OptionTooltip data={customer.eori || (customer as any).identificationNumber} />
                    </Col>
                    <Col>
                        <OptionTooltip data={customer.name} />
                    </Col>
                    <Col>
                        <OptionTooltip data={customer.contact?.email} />
                    </Col>
                    <Col>
                        <OptionTooltip data={customer.contact?.phoneNumber} />
                    </Col>
                </Row>
            ),
        }));

        return [
            {
                label: <span>Customers</span>,
                options: options,
            },
        ];
    }, [customers]);

    return (
        <>
            <FormAutocomplete
                value={value}
                dropdownMatchSelectWidth={dropdownMatchSelectWidth}
                onBlur={onBlur}
                style={inputStyle}
                disabled={disableSelect}
                options={customerOptions}
                fieldMeta={fieldMeta}
                onSelect={(value: string) => {
                    handleSelectCustomer(value);
                }}
                onClear={() => {
                    setCustomer(undefined);
                    onClear?.();
                }}
                inputPlaceholder={placeholder ?? t(`SearchCustomer`)}
                onSearch={(e) => search(e)}
            />
        </>
    );
};

export default CustomerAutocomplete;
